import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, ValidationActionType } from 'models/enum';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import { areAllStepsComplete, canSelectStep, getMaxSelectableStep } from 'components/features/chart/special-charts/shared/stepNavigation';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { FIELD_CONFIGS, FIELD_CONFIG_MAP, FieldConfig, FormField, FormFieldLabel, LAST_STEP_INDEX, STEP_CONFIGS, SectionTitle, TRANSFUSION_REACTION_YES } from './constants';
import BloodProductAdministrationRecordView, { BloodProductAdministrationRecordViewProps } from './BloodProductAdministrationRecordView';
import { ProductRequestRow } from './ProductRequestSidebar';
import { TransfusionDetailEntry } from './TransfusionDetailsSidebar';

export interface BloodProductAdministrationRecordProps extends ChartComponentProps {
  navigateToSavedPatientCharting: VoidFunction;
  authorName?: string;
}

// The Provider / Nurse value is derived from the signed-in user and never rendered on the form.
export const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;

  return chartService.buildPlainRecord({
    formField: FormField.PROVIDER_NURSE,
    title: FormFieldLabel.PROVIDER_NURSE,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

const buildFormField = (fieldConfig: FieldConfig): ChartMetaFormField => ({
  ...chartHelper.createFormField({ name: fieldConfig.name, type: fieldConfig.type, label: fieldConfig.label }),
  isRequired: Boolean(fieldConfig.isRequired),
  value: ''
});

export const buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
  const dataMap = new Map<string, ChartMetaFormField>();

  FIELD_CONFIGS.forEach((fieldConfig) => dataMap.set(fieldConfig.name, buildFormField(fieldConfig)));

  return dataMap;
};

export const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;

  const { value } = formField;

  if (Array.isArray(value)) return value.length > 0;
  if (formField.inputType === FormFieldInputType.CHECK_BOX) return Boolean(formField.chartContent?.some((item) => item.selected));

  return String(value ?? '').trim().length > 0;
};

const buildRequiredSanitizedFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => {
  const sanitizedMap = new Map<string, ChartMetaFormField>();

  formFieldMap.forEach((field, name) => {
    sanitizedMap.set(name, {
      ...field,
      isRequired: false,
      errors: (field.errors || []).filter((error) => error?.type !== ValidationActionType.REQUIRED)
    });
  });

  return sanitizedMap;
};

// Only values the user actually filled in are saved to the Saved Special Chart.
const buildRowRecord = (formField: string, title: string, content: string) => (content ? chartService.buildPlainRecord({ formField, title, content }) : null);

// The free-text box nested under an "Other" option is folded into its parent record instead of standing on its own.
const getNestedOtherFieldNames = (formFieldMap: Map<string, ChartMetaFormField>) =>
  new Set(Array.from(formFieldMap.values()).flatMap((field) => (field.chartContent || []).map((item) => item.nestedFieldId).filter(Boolean)));

// Selections are saved as a single "title: value, value" record so the Saved Special Chart shows the group they belong to.
const buildSelectionRecord = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: string, formField: ChartMetaFormField) => {
  const content = (formField.chartContent || [])
    .filter((item) => item.selected && item.value)
    .map((item) => (item.nestedFieldId ? String(formFieldMap.get(item.nestedFieldId)?.value ?? '').trim() : item.label))
    .filter(Boolean)
    .join(', ');
  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);

  return content ? chartService.buildPlainRecord({ formField: fieldName, title: fieldConfig?.recordTitle || fieldConfig?.label || '', content }) : null;
};

const buildFieldRecord = (formFieldMap: Map<string, ChartMetaFormField>, nestedOtherFieldNames: Set<string>, fieldName: string) => {
  if (nestedOtherFieldNames.has(fieldName)) return null;

  const formField = formFieldMap.get(fieldName);
  if (!formField) return null;

  if (formField.inputType === FormFieldInputType.CHECK_BOX || formField.inputType === FormFieldInputType.RADIO_CHOICE) {
    return buildSelectionRecord(formFieldMap, fieldName, formField);
  }

  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
  return chartService.buildPatientRecord(formFieldMap, fieldName, fieldConfig?.recordTitle || fieldConfig?.label || undefined);
};

const BloodProductAdministrationRecord = (props: BloodProductAdministrationRecordProps) => {
  const {
    authorName,
    chartingTime,
    displayAuthoringData,
    enableDisplayRecordsButton,
    formFieldMap,
    formSubmittedCount,
    initState,
    isAuthor,
    navigateToSavedPatientCharting,
    resetFormSubmitted,
    resetSubsetOfFormFields,
    saveChart,
    student
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [productRequests, setProductRequests] = useState<ProductRequestRow[]>([]);
  const [transfusionDetails, setTransfusionDetails] = useState<TransfusionDetailEntry[]>([]);
  const [isProductRequestSidebarOpen, setIsProductRequestSidebarOpen] = useState(false);
  const [isTransfusionSidebarOpen, setIsTransfusionSidebarOpen] = useState(false);
  const [productRequestResetCount, setProductRequestResetCount] = useState(0);
  const [transfusionResetCount, setTransfusionResetCount] = useState(0);

  const safeFormFieldMap = formFieldMap || buildDefaultFormFields();
  const safeFormFieldMapWithoutRequiredErrors = useMemo(() => buildRequiredSanitizedFieldMap(safeFormFieldMap), [safeFormFieldMap]);

  useEffect(() => {
    initState(buildDefaultFormFields());
  }, [initState]);

  const isFieldRequired = useCallback(
    (fieldName: string): boolean => {
      if (fieldName === FormField.DESCRIBE_REACTION) {
        return String(safeFormFieldMap.get(FormField.TRANSFUSION_REACTION)?.value ?? '') === TRANSFUSION_REACTION_YES;
      }

      return Boolean(FIELD_CONFIG_MAP.get(fieldName)?.isRequired);
    },
    [safeFormFieldMap]
  );

  const isStepComplete = useCallback(
    (stepIndex: number): boolean => STEP_CONFIGS[stepIndex].fields.every((fieldName) => !isFieldRequired(fieldName) || hasFieldValue(safeFormFieldMap.get(fieldName))),
    [isFieldRequired, safeFormFieldMap]
  );

  const stepAccess = { stepCount: STEP_CONFIGS.length, furthestStep, isStepComplete };
  // The submit gate covers the whole form, not just the last step, so a form emptied through the
  // indicators can never be submitted.
  const isFinalStepReady = areAllStepsComplete(stepAccess);
  // A step is only ticked once the user has moved past it AND its data still stands, so clearing a
  // step withdraws its checkmark without ticking steps that were never visited.
  const completedSteps = STEP_CONFIGS.map((_, index) => index < furthestStep && isStepComplete(index));
  const maxSelectableStep = getMaxSelectableStep(stepAccess);

  // Reset is enabled once any value has been added on the active step.
  const canReset =
    STEP_CONFIGS[activeStep].fields.some((fieldName) => hasFieldValue(safeFormFieldMap.get(fieldName))) ||
    (activeStep === 2 && productRequests.length > 0) ||
    (activeStep === 4 && transfusionDetails.length > 0);

  const buildFragment = useCallback((): ChartFragment => {
    const nestedOtherFieldNames = getNestedOtherFieldNames(safeFormFieldMap);
    const buildStepRecords = (fieldNames: string[]) => fieldNames.flatMap((fieldName) => buildFieldRecord(safeFormFieldMap, nestedOtherFieldNames, fieldName) ?? []);

    // Each row is titled with its component so the Saved Special Chart keeps every request instead of collapsing them onto one.
    const productRequestRecords = productRequests
      .filter((row) => row.active)
      .flatMap((row) =>
        [
          buildRowRecord('productRequestNumberOfUnits', `${row.componentDesired} - No. of Units`, row.numberOfUnits),
          buildRowRecord('productRequestDay', `${row.componentDesired} - Day`, row.day),
          buildRowRecord('productRequestTime', `${row.componentDesired} - Time`, row.time)
        ].filter(Boolean)
      );

    const transfusionDetailRecords = transfusionDetails
      .filter((entry) => entry.active)
      .flatMap((entry) =>
        [
          buildRowRecord('transfusionDetailsDay', 'Day', entry.day),
          buildRowRecord('transfusionDetailsTimeBegun', 'Time Begun', entry.timeBegun),
          buildRowRecord('transfusionDetailsTimeCompleted', 'Time Completed', entry.timeCompleted),
          ...entry.vitalSigns.flatMap((vitalSign) => [
            buildRowRecord('transfusionDetailsBefore', `${vitalSign.vitalSign} - Before Transfusion`, vitalSign.beforeTransfusion),
            buildRowRecord('transfusionDetailsFifteenMinutes', `${vitalSign.vitalSign} - 15 Minutes After Start of Transfusion`, vitalSign.fifteenMinutesAfter),
            buildRowRecord('transfusionDetailsImmediatelyAfter', `${vitalSign.vitalSign} - Immediately After Transfusion`, vitalSign.immediatelyAfter)
          ])
        ].filter(Boolean)
      );

    // The drawer entries belong to the step that collected them.
    const drawerRecordsByStep = {
      [SectionTitle.PROVIDER_BLOOD_PRODUCT_REQUEST]: productRequestRecords,
      [SectionTitle.TRANSFUSION_ADMINISTRATION]: transfusionDetailRecords
    };

    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.BLOOD_PRODUCT_ADMINISTRATION_RECORD,
      records: [
        { sectionTitle: SectionTitle.GENERAL_INFORMATION, records: [buildProviderNurseRecord(isAuthor, authorName, student)].filter(Boolean) },
        ...STEP_CONFIGS.map((stepConfig) => ({
          sectionTitle: stepConfig.title,
          records: [...buildStepRecords(stepConfig.fields), ...(drawerRecordsByStep[stepConfig.title] ?? [])]
        }))
      ]
    };

    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  }, [authorName, chartingTime, isAuthor, productRequests, safeFormFieldMap, student, transfusionDetails]);

  const handleSaveClick = useCallback(() => {
    saveChart([buildFragment()], {
      formFieldMap: safeFormFieldMapWithoutRequiredErrors,
      defaultFormFields: buildDefaultFormFields(),
      // Students are taken to the Saved Special Chart once the chart is complete. Authors stay put,
      // because the save is what enables "Return with data".
      afterSave: () => {
        if (!isAuthor) navigateToSavedPatientCharting();
      }
    });
  }, [buildFragment, isAuthor, navigateToSavedPatientCharting, safeFormFieldMapWithoutRequiredErrors, saveChart]);

  const handleSelectStep = useCallback(
    (stepIndex: number) => {
      if (!canSelectStep(stepIndex, { stepCount: STEP_CONFIGS.length, furthestStep, isStepComplete })) return;
      setActiveStep(stepIndex);
      resetFormSubmitted();
    },
    [furthestStep, isStepComplete, resetFormSubmitted]
  );

  const handleBackStep = useCallback(() => {
    if (activeStep === 0) return;
    setActiveStep(activeStep - 1);
    resetFormSubmitted();
  }, [activeStep, resetFormSubmitted]);

  const handleContinueStep = useCallback(() => {
    if (activeStep === LAST_STEP_INDEX) return;

    const nextStep = activeStep + 1;
    setFurthestStep((previousStep) => Math.max(previousStep, nextStep));
    setActiveStep(nextStep);
    resetFormSubmitted();
  }, [activeStep, resetFormSubmitted]);

  const handleResetStep = useCallback(() => {
    const activeStepFields = STEP_CONFIGS[activeStep].fields
      .filter((fieldName) => FIELD_CONFIG_MAP.has(fieldName))
      .map((fieldName) => safeFormFieldMap.get(fieldName))
      .filter(Boolean);

    if (activeStep === 2) {
      setProductRequests([]);
      setProductRequestResetCount((previousCount) => previousCount + 1);
    }
    if (activeStep === 4) {
      setTransfusionDetails([]);
      setTransfusionResetCount((previousCount) => previousCount + 1);
    }

    resetSubsetOfFormFields(activeStepFields);
    resetFormSubmitted();
  }, [activeStep, resetFormSubmitted, resetSubsetOfFormFields, safeFormFieldMap]);

  const viewProps: BloodProductAdministrationRecordViewProps = {
    activeStep,
    canContinue: isStepComplete(activeStep),
    canReset,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton: Boolean(enableDisplayRecordsButton),
    formFieldMap: safeFormFieldMapWithoutRequiredErrors,
    formSubmittedCount,
    isAuthor: Boolean(isAuthor),
    isFinalStepReady,
    maxSelectableStep,
    isProductRequestSidebarOpen,
    isTransfusionSidebarOpen,
    productRequestResetCount,
    productRequests,
    transfusionDetails,
    transfusionResetCount,
    // The drawers retain their values so entries can be edited; saving replaces the rows.
    onAddProductRequests: (rows: ProductRequestRow[]) => {
      setProductRequests(rows);
      setIsProductRequestSidebarOpen(false);
    },
    onAddTransfusionDetail: (entry: TransfusionDetailEntry | null) => {
      setTransfusionDetails(entry ? [entry] : []);
      setIsTransfusionSidebarOpen(false);
    },
    onBackStep: handleBackStep,
    // Each drawer restores its own last-saved values on close, so nothing is signalled from here.
    onCloseProductRequestSidebar: () => setIsProductRequestSidebarOpen(false),
    onCloseTransfusionSidebar: () => setIsTransfusionSidebarOpen(false),
    onContinueStep: handleContinueStep,
    onOpenProductRequestSidebar: () => setIsProductRequestSidebarOpen(true),
    onOpenTransfusionSidebar: () => setIsTransfusionSidebarOpen(true),
    onResetStep: handleResetStep,
    onSaveClick: handleSaveClick,
    onSelectStep: handleSelectStep
  };

  return <BloodProductAdministrationRecordView {...viewProps} />;
};

export { BloodProductAdministrationRecord as BaseBloodProductAdministrationRecord };

const mapStateToProps = (state) => ({
  authorName: studentSelectors.getAuthorName(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(BloodProductAdministrationRecord);
